import { MarkdownFence } from '@squareup/dex-feature-shared-markdown-fence';
import { MarkdownInlineCode } from '@squareup/dex-ui-shared-markdown-components';
import React, { FC, PropsWithChildren, isValidElement } from 'react';

interface MdxPreProps {
  children?: React.ReactNode;
}

// Reuse the same fence renderer the Contentful path goes through (see
// `basicFence` in @squareup/dex-ui-shared-markdown-components). It wraps
// dex-ui's <CodeBlock> with floatingHeader, the action-group toolbar, and
// the expand-on-overflow + footer behaviour. Anything less re-implemented
// here drifts visually from Contentful pages — Emma flagged the divider
// strip beneath the copy bar that you get with a plain <CodeBlock>.
const MdxMdxCodeBlock: FC<MdxPreProps> = ({ children }) => {
  // MDX wraps code blocks in <pre><code className="language-X">...</code></pre>
  // Extract the language and content from the child <code> element.
  if (isValidElement(children)) {
    const props = children.props as Record<string, unknown>;
    const className = (props.className as string) || '';
    const language = className.replace('language-', '') || 'bash';
    const content =
      typeof props.children === 'string'
        ? props.children
        : String(props.children || '');

    return <MarkdownFence content={content} language={language} />;
  }

  return <pre>{children}</pre>;
};

MdxMdxCodeBlock.displayName = 'MdxCodeBlock';

interface MdxCodeProps {
  children?: React.ReactNode;
  className?: string;
}

const MdxMdxInlineCode: FC<PropsWithChildren<MdxCodeProps>> = ({
  children,
  className,
}) => {
  // If className starts with 'language-', this is inside a <pre> and
  // will be handled by MdxMdxCodeBlock. Just pass through.
  if (className?.startsWith('language-')) {
    return <code className={className}>{children}</code>;
  }

  const content =
    typeof children === 'string' ? children : String(children || '');
  return <MarkdownInlineCode content={content} />;
};

MdxMdxInlineCode.displayName = 'MdxInlineCode';

export { MdxMdxCodeBlock, MdxMdxInlineCode };
